import JsBarcode from "jsbarcode";
import { checksum as upcChecksum } from "jsbarcode/src/barcodes/EAN_UPC/UPC";

import { Modal } from "~/jose_modern/modal";
import { Flash } from "~/jose_modern/flash";
import { BARCODE_CONSTANTS, getBarcodeFormat } from "./constants.js";

// Re-export constants and getBarcodeFormat for backwards compatibility
export { BARCODE_CONSTANTS, getBarcodeFormat };

// Label type configurations for route selection
const LABEL_CONFIGS = {
  recipe: {
    resourceName: "recipe_labels",
    getLabelData: () => window.recipe_label,
  },
  supplement: {
    resourceName: "supplement_labels",
    getLabelData: () => window.supplement_label,
    onBarcodeUpdated: (barcode) => {
      window.dispatchEvent(new CustomEvent("supplement:barcode-updated", { detail: { barcode } }));
    },
  },
};

// Current config - set during setup()
let currentConfig = LABEL_CONFIGS.recipe;

const getBarcodePath = (labelId) => `/${currentConfig.resourceName}/${labelId}/barcode`;
const getToggleDisplayPath = (labelId) => `${getBarcodePath(labelId)}/toggle_display`;
const getDefaultCodePath = (labelId) => `${getBarcodePath(labelId)}/default_code`;

// Helper to get label data using current config
const getLabelData = () => currentConfig.getLabelData();

// Import constants for local use
const {
  CODE_128,
  EAN_13,
  UPC_A,
  DEFAULT_TYPE: DEFAULT_BARCODE_TYPE,
  MIN_HEIGHT,
  MAX_HEIGHT,
  HEIGHT_STEP,
  MIN_WIDTH,
  MAX_WIDTH,
  WIDTH_STEP,
} = BARCODE_CONSTANTS;
const DEFAULT_LABEL_WIDTH = 237;

// A code128 barcode can actually be as short as 1 character, but we should set
// a minimum that is somewhat sane for small/independent stores stocking these products.
const MIN_BARCODE_CHARS = 4;

// DOM Selectors for the "edit" Modal
const MODAL = "#barcode-modal";
const MODAL_SVG = "#barcode-modal-svg";
const WIDTH_SLIDER = "#barcode-width-slider";
const HEIGHT_SLIDER = "#barcode-height-slider";
const WIDTH_INPUT = "#barcode-width";
const HEIGHT_INPUT = "#barcode-height";
const WIDTH_VALUE = "#barcode-width-value";
const HEIGHT_VALUE = "#barcode-height-value";
const BARCODE_TYPE = "#barcode-type";
const BARCODE_CODE = "#barcode-code";
const BARCODE_CODE_ERROR = "#barcode-code-error";
const CODE_CAUTION_ICON = "#code-caution-icon";
const WIDTH_WARNING = "#barcode-width-warning";
const WIDTH_CAUTION_ICON = "#width-caution-icon";
const MAX_WIDTH_INDICATOR = "#max-width-indicator";
const SIZE_RESET = "#barcode-size-reset";
const CODE_RESET = "#barcode-code-reset";
const APPLY_BUTTON = "#barcode-apply-changes";

// DOM Selectors for Label, Sidebar, Barcode
const SIDEBAR_BARCODE_TOGGLE = "#hide-barcode-toggle";
const SIDEBAR_BARCODE_HINT = "#barcode-hint";
const SIDEBAR_BARCODE_EDIT = "#edit-barcode-link";

const LABEL_DIV = "#label > div"; // nutrition facts table

const BARCODE_LABEL_SVG = "#barcode-label-svg";
const BARCODE_CONTAINER = "#barcode-container";

// Constants from Ruby instead of re-hardcoding here
let defaultWidth = null;
let defaultHeight = null;

const getDefaultWidth = () => {
  if (defaultWidth === null) {
    const btn = document.querySelector(SIZE_RESET);
    defaultWidth = btn ? parseInt(btn.getAttribute("data-default-width"), 10) : 1;
  }
  return defaultWidth;
};

const getDefaultHeight = () => {
  if (defaultHeight === null) {
    const btn = document.querySelector(SIZE_RESET);
    defaultHeight = btn ? parseInt(btn.getAttribute("data-default-height"), 10) : 50;
  }
  return defaultHeight;
};

let font = undefined;

// ==========================================
// Module state
// ==========================================

let hasFormatError = false;
let validateTimer = null;

const formatSpecificCodes = {
  code128: "",
  upc_a: "",
  ean13: "",
};

// Track bound event handlers for cleanup
let boundHandlers = {
  checkboxChange: null,
  editButtonClick: null,
  codeResetClick: null,
  sizeResetClick: null,
  typeInputFocus: null,
  typeInputChange: null,
  codeInputHandler: null,
  applyButtonClick: null,
  widthSliderInput: null,
  heightSliderInput: null,
};

function scheduleValidation(delay = 50) {
  if (validateTimer) clearTimeout(validateTimer);
  validateTimer = setTimeout(validateBarcode, delay);
}

// ==========================================
// Sidebar checkbox behavior
// ==========================================

const setupCheckbox = function () {
  const checkbox = document.querySelector(SIDEBAR_BARCODE_TOGGLE);
  if (!checkbox) return;

  if (boundHandlers.checkboxChange) {
    checkbox.removeEventListener("change", boundHandlers.checkboxChange);
  }

  boundHandlers.checkboxChange = function () {
    const hideBarcode = this.checked;
    saveHideBarcodeSetting(hideBarcode);
  };

  checkbox.addEventListener("change", boundHandlers.checkboxChange);
};

const setupEditButton = () => {
  // Use event delegation on document for dynamic elements
  if (boundHandlers.editButtonClick) {
    document.removeEventListener("click", boundHandlers.editButtonClick);
  }

  boundHandlers.editButtonClick = function (e) {
    const editLink = e.target.closest(SIDEBAR_BARCODE_EDIT);
    if (!editLink) return;

    e.preventDefault();

    Modal.show(MODAL, {
      onDismiss: (el) => {
        if (!el || !el.hasAttribute("data-no-revert")) {
          restoreSavedBarcode();
        }
      },
    });

    setupModalBindings();
    renderModalPreview();
    validateBarcode();
  };

  document.addEventListener("click", boundHandlers.editButtonClick);
};

function saveHideBarcodeSetting(hideBarcode) {
  const labelData = getLabelData();
  labelData.hide_barcode = hideBarcode;
  setBarcodeVisibility({ isVisible: !hideBarcode });

  const labelId = labelData.id;

  fetch(getToggleDisplayPath(labelId), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
    body: JSON.stringify({ hide_barcode: hideBarcode }),
  })
    .then((response) => {
      if (!response.ok) throw response;
      return response.json();
    })
    .then((data) => {
      if (!hideBarcode) {
        handleSavedBarcode(data.barcode);
        renderLabelBarcodeSection();
      }
    })
    .catch((error) => {
      handleFetchError(error);
      setBarcodeVisibility({ isVisible: !getLabelData().hide_barcode });
    });
}

/// Set the visibility of the barcode section,
/// and in the sidebar we swap between the hint icon vs the edit button
function setBarcodeVisibility({ isVisible }) {
  [SIDEBAR_BARCODE_EDIT, BARCODE_CONTAINER].forEach((selector) => {
    const el = document.querySelector(selector);
    if (el) el.classList.toggle("is-hidden", !isVisible);
  });
  [SIDEBAR_BARCODE_HINT].forEach((selector) => {
    const el = document.querySelector(selector);
    if (el) el.classList.toggle("is-hidden", isVisible);
  });
}

/// handleSavedBarcode is called when we create or update the barcode.
function handleSavedBarcode(barcode) {
  if (barcode) {
    const labelData = getLabelData();
    labelData.primary_barcode = barcode;
    populateModalForm(barcode);
    updateSavedBarcode(barcode);

    // Notify Vue store for supplement labels
    if (currentConfig.onBarcodeUpdated) {
      currentConfig.onBarcodeUpdated(barcode);
    }
  }
}

// ==========================================
// Barcode generation / SVG stuff
// ==========================================

/// Render the barcode in the main label display
const renderLabelBarcode = () => {
  const displaySvg = document.querySelector(BARCODE_LABEL_SVG);

  if (!displaySvg) {
    console.warn("Display SVG element not found for barcode rendering");
    return;
  }

  try {
    const barcodeData = getLabelData().primary_barcode;
    if (barcodeData) {
      renderBarcodeToSvg(displaySvg, barcodeData);
    }
  } catch (e) {
    console.error("Uncaught error while rendering label barcode:", e);
  }
};

function renderModalPreview() {
  const svg = document.querySelector(MODAL_SVG);
  if (!svg) {
    console.warn("Modal preview SVG element not found");
    return;
  }

  const codeInput = document.querySelector(BARCODE_CODE);
  const typeInput = document.querySelector(BARCODE_TYPE);
  const widthInput = document.querySelector(WIDTH_INPUT);
  const heightInput = document.querySelector(HEIGHT_INPUT);

  const code = codeInput?.value || "";
  const type = typeInput?.value || DEFAULT_BARCODE_TYPE;
  const width = parseInt(widthInput?.value, 10) || getDefaultWidth();
  const height = parseInt(heightInput?.value, 10) || getDefaultHeight();

  try {
    const success = renderBarcodeToSvg(svg, { code, type, width, height });
    if (success) {
      updateMaxWidthIndicator();
      scheduleValidation(50);
    }
  } catch (e) {
    console.warn("Failed to render preview barcode:", e);
    const readableType = getReadableTypeName(type);
    renderErrorInSvg(svg, `Invalid barcode value for ${readableType}`);
    hasFormatError = true;
  }
}

function renderBarcodeToSvg(svg, options) {
  if (!svg) return false;

  clearSvgContents(svg);

  const { code, type, width, height, errorMessage } = options;

  if (!code || code.trim() === "") {
    renderErrorInSvg(svg, errorMessage || "Please enter a barcode value");
    hasFormatError = true;
    return false;
  }

  /// We sanitize the code before validation/rendering.
  /// (For UPC/EAN we will leave spaces/dashes in the saved field, as the user might want them.)
  let codeForRendering = type === CODE_128 ? code.trim() : removeSpacesAndDashes(code);

  // Pre-validate UPC/EAN codes before trying to render
  const validation = preValidateBarcodeFormat(codeForRendering, type);
  if (!validation.isValid) {
    renderErrorInSvg(svg, validation.errorMessage);
    hasFormatError = true;
    return false;
  }

  try {
    const format = (type === UPC_A ? "upc" : type) || DEFAULT_BARCODE_TYPE;
    if (!font) {
      const fontElement = svg.closest(".label-font");
      if (fontElement) {
        const computed = window.getComputedStyle(fontElement);
        font = computed && computed.fontFamily;
      }
    }
    JsBarcode(svg, codeForRendering, {
      format,
      height: height || getDefaultHeight(),
      width: width || getDefaultWidth(),
      margin: 0,
      displayValue: true,
      fontSize: 11,
      ...(font ? { font: font } : {}),
    });

    /// min-width may have been set to make room to show an error; ensure it is unset.
    svg.style.minWidth = "unset";

    hasFormatError = false;
    return true;
  } catch (e) {
    console.info("Failed to render barcode:", e);
    const readableType = getReadableTypeName(type);
    renderErrorInSvg(svg, `Invalid barcode value for ${readableType}`);
    hasFormatError = true;
    return false;
  }
}

function renderErrorInSvg(svg, message) {
  svg.style.minWidth = "100%";
  svg.innerHTML = `<text x="50%" y="50%" text-anchor="middle" fill="#f14668">${message}</text>`;
  hideWidthWarningAndIndicators();
}

function clearSvgContents(svg) {
  while (svg.firstChild) {
    svg.removeChild(svg.firstChild);
  }
}

// ==========================================
// Modal: type and code fields
// ==========================================

function setupModalBindings() {
  initializeTypeField();

  setupModalSliders();
  setupCodeInput();
  setupTypeInput();
  setupResetButtons();
  setupApplyButton();

  renderModalPreview();
  scheduleValidation(100);
}

function initializeTypeField() {
  const typeInput = document.querySelector(BARCODE_TYPE);
  const codeInput = document.querySelector(BARCODE_CODE);

  const currentType = typeInput?.value || DEFAULT_BARCODE_TYPE;
  formatSpecificCodes[currentType] = codeInput?.value || "";

  toggleCodeResetButtonVisibility(currentType);
  updateCodePlaceholder(currentType);
  showHelpTextForType(currentType);
}

// We only show the reset button for code128 which has a generated default to reset to.
function toggleCodeResetButtonVisibility(type) {
  const resetBtn = document.querySelector(CODE_RESET);
  if (resetBtn) {
    resetBtn.classList.toggle("is-hidden", type !== CODE_128);
  }
}

function showHelpTextForType(type) {
  // Hide all the help texts then show the relevant one.
  document.querySelectorAll(".barcode-help-text").forEach((el) => el.classList.add("is-hidden"));
  const helpEl = document.querySelector(`#help-${type}.barcode-help-text`);
  if (helpEl) helpEl.classList.remove("is-hidden");
}

function updateCodePlaceholder(type) {
  const codeInput = document.querySelector(BARCODE_CODE);
  if (!codeInput) return;

  const placeholders = {
    [CODE_128]: "Enter a unique code",
    [UPC_A]: "Enter a registered UPC-A code",
    [EAN_13]: "Enter a registered EAN-13 code",
  };

  codeInput.placeholder = placeholders[type] || placeholders.code128;
}

// ==========================================
// Modal: handlers for buttons and fields
// ==========================================

function setupResetButtons() {
  const codeResetBtn = document.querySelector(CODE_RESET);
  const sizeResetBtn = document.querySelector(SIZE_RESET);

  // Reset code button (only shown for code128)
  if (codeResetBtn) {
    if (boundHandlers.codeResetClick) {
      codeResetBtn.removeEventListener("click", boundHandlers.codeResetClick);
    }
    boundHandlers.codeResetClick = function (e) {
      e.preventDefault();
      fetchBarcodeCode();
    };
    codeResetBtn.addEventListener("click", boundHandlers.codeResetClick);
  }

  // Size reset button - resets both width and height
  if (sizeResetBtn) {
    if (boundHandlers.sizeResetClick) {
      sizeResetBtn.removeEventListener("click", boundHandlers.sizeResetClick);
    }
    boundHandlers.sizeResetClick = function (e) {
      e.preventDefault();

      const defaultW = parseInt(this.dataset.defaultWidth, 10) || getDefaultWidth();
      const defaultH = parseInt(this.dataset.defaultHeight, 10) || getDefaultHeight();

      const widthSlider = document.querySelector(WIDTH_SLIDER);
      const heightSlider = document.querySelector(HEIGHT_SLIDER);
      const widthInput = document.querySelector(WIDTH_INPUT);
      const heightInput = document.querySelector(HEIGHT_INPUT);
      const widthValue = document.querySelector(WIDTH_VALUE);
      const heightValue = document.querySelector(HEIGHT_VALUE);

      if (widthSlider) widthSlider.value = defaultW;
      if (widthInput) widthInput.value = defaultW;
      if (widthValue) widthValue.textContent = defaultW;

      if (heightSlider) heightSlider.value = defaultH;
      if (heightInput) heightInput.value = defaultH;
      if (heightValue) heightValue.textContent = defaultH;

      this.classList.add("is-invisible");
      renderModalPreview();
    };
    sizeResetBtn.addEventListener("click", boundHandlers.sizeResetClick);
  }
}

function setupTypeInput() {
  const typeInput = document.querySelector(BARCODE_TYPE);
  if (!typeInput) return;

  let previousType = typeInput.value || DEFAULT_BARCODE_TYPE;

  if (boundHandlers.typeInputFocus) {
    typeInput.removeEventListener("focus", boundHandlers.typeInputFocus);
  }
  boundHandlers.typeInputFocus = function () {
    previousType = this.value;
  };
  typeInput.addEventListener("focus", boundHandlers.typeInputFocus);

  if (boundHandlers.typeInputChange) {
    typeInput.removeEventListener("change", boundHandlers.typeInputChange);
  }
  boundHandlers.typeInputChange = function () {
    const newType = this.value;
    const codeInput = document.querySelector(BARCODE_CODE);
    const currentCode = codeInput?.value || "";

    if (previousType) {
      formatSpecificCodes[previousType] = currentCode;
    }
    previousType = newType;

    const codeResetBtn = document.querySelector(CODE_RESET);
    if (codeResetBtn) {
      codeResetBtn.classList.toggle("is-hidden", newType !== CODE_128);
    }

    showHelpTextForType(newType);
    updateCodePlaceholder(newType);

    let newCode = formatSpecificCodes[newType];

    const updateCode = (code) => {
      if (codeInput) codeInput.value = code;
      formatSpecificCodes[newType] = code;
      renderModalPreview();
      scheduleValidation(50);
    };

    // For Code128, fetch default if needed
    if (newType === DEFAULT_BARCODE_TYPE && !newCode) {
      fetchBarcodeCode(updateCode);
    } else {
      updateCode(newCode);
    }
  };
  typeInput.addEventListener("change", boundHandlers.typeInputChange);
}

function setupCodeInput() {
  const codeInput = document.querySelector(BARCODE_CODE);
  if (!codeInput) return;

  if (boundHandlers.codeInputHandler) {
    codeInput.removeEventListener("input", boundHandlers.codeInputHandler);
    codeInput.removeEventListener("change", boundHandlers.codeInputHandler);
    codeInput.removeEventListener("blur", boundHandlers.codeInputHandler);
  }

  boundHandlers.codeInputHandler = function () {
    const typeInput = document.querySelector(BARCODE_TYPE);
    const currentType = typeInput?.value || DEFAULT_BARCODE_TYPE;
    formatSpecificCodes[currentType] = this.value;

    scheduleValidation(10);
    renderModalPreview();
  };

  codeInput.addEventListener("input", boundHandlers.codeInputHandler);
  codeInput.addEventListener("change", boundHandlers.codeInputHandler);
  codeInput.addEventListener("blur", boundHandlers.codeInputHandler);
}

function setupApplyButton() {
  const applyBtn = document.querySelector(APPLY_BUTTON);
  if (!applyBtn) return;

  if (boundHandlers.applyButtonClick) {
    applyBtn.removeEventListener("click", boundHandlers.applyButtonClick);
  }

  boundHandlers.applyButtonClick = function (e) {
    if (!validateBarcode()) {
      e.preventDefault();
      return false;
    }

    const labelId = getLabelData().id;
    const typeInput = document.querySelector(BARCODE_TYPE);
    const codeInput = document.querySelector(BARCODE_CODE);

    const height = getSliderValue(HEIGHT_SLIDER);
    const type = typeInput?.value || DEFAULT_BARCODE_TYPE;
    let code = codeInput?.value || "";
    if (type === CODE_128 && typeof code === "string") {
      code = code.trim();
    }

    fetch(getBarcodePath(labelId), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({
        barcode: {
          type,
          code,
          height,
          width: getSliderValue(WIDTH_SLIDER),
        },
      }),
    })
      .then((response) => {
        if (!response.ok) throw response;
        return response.json();
      })
      .then((data) => {
        handleSavedBarcode(data.barcode);

        updateLabelBarcodeContainerWidth();
        renderLabelBarcode();

        handleSuccess(data);
      })
      .catch(handleFetchError);
  };

  applyBtn.addEventListener("click", boundHandlers.applyButtonClick);
}

function populateModalForm(barcode) {
  const typeInput = document.querySelector(BARCODE_TYPE);
  const codeInput = document.querySelector(BARCODE_CODE);
  const widthInput = document.querySelector(WIDTH_INPUT);
  const heightInput = document.querySelector(HEIGHT_INPUT);
  const widthSlider = document.querySelector(WIDTH_SLIDER);
  const heightSlider = document.querySelector(HEIGHT_SLIDER);
  const widthValue = document.querySelector(WIDTH_VALUE);
  const heightValue = document.querySelector(HEIGHT_VALUE);

  if (typeInput) typeInput.value = barcode.type || DEFAULT_BARCODE_TYPE;
  if (codeInput) codeInput.value = barcode.code || "";

  const w = barcode.width || getDefaultWidth();
  const h = barcode.height || getDefaultHeight();

  if (widthInput) widthInput.value = w;
  if (heightInput) heightInput.value = h;
  if (widthSlider) widthSlider.value = w;
  if (heightSlider) heightSlider.value = h;
  if (widthValue) widthValue.textContent = w;
  if (heightValue) heightValue.textContent = h;
}

// ========================================================================
// Modal: supporting code for 'cancel' reverting changes to saved state
// ========================================================================

function updateSavedBarcode(barcode) {
  const modal = document.querySelector(MODAL);
  if (modal) {
    modal.setAttribute("data-saved-barcode", JSON.stringify(barcode));
  }
}

function restoreSavedBarcode() {
  const modal = document.querySelector(MODAL);
  const saved = modal?.getAttribute("data-saved-barcode");
  if (!saved) return;
  try {
    const barcode = JSON.parse(saved);
    populateModalForm(barcode);
  } catch (e) {
    console.warn("Could not parse saved barcode JSON:", e);
  }
}

// ==========================================
// Modal: sliders (native HTML range inputs)
// ==========================================

function setupModalSliders() {
  const widthSlider = document.querySelector(WIDTH_SLIDER);
  const heightSlider = document.querySelector(HEIGHT_SLIDER);
  const widthInput = document.querySelector(WIDTH_INPUT);
  const heightInput = document.querySelector(HEIGHT_INPUT);
  const widthValue = document.querySelector(WIDTH_VALUE);
  const heightValue = document.querySelector(HEIGHT_VALUE);

  // Configure slider attributes
  if (widthSlider) {
    widthSlider.type = "range";
    widthSlider.min = MIN_WIDTH;
    widthSlider.max = MAX_WIDTH;
    widthSlider.step = WIDTH_STEP;

    // Set initial value from hidden input
    const initialWidth = widthInput?.value || getDefaultWidth();
    widthSlider.value = initialWidth;
    if (widthValue) widthValue.textContent = initialWidth;

    if (boundHandlers.widthSliderInput) {
      widthSlider.removeEventListener("input", boundHandlers.widthSliderInput);
    }
    boundHandlers.widthSliderInput = function () {
      const val = this.value;
      if (widthInput) widthInput.value = val;
      if (widthValue) widthValue.textContent = val;
      updateSizeResetButtonVisibility();
      renderModalPreview();
    };
    widthSlider.addEventListener("input", boundHandlers.widthSliderInput);
  }

  if (heightSlider) {
    heightSlider.type = "range";
    heightSlider.min = MIN_HEIGHT;
    heightSlider.max = MAX_HEIGHT;
    heightSlider.step = HEIGHT_STEP;

    // Set initial value from hidden input
    const initialHeight = heightInput?.value || getDefaultHeight();
    heightSlider.value = initialHeight;
    if (heightValue) heightValue.textContent = initialHeight;

    if (boundHandlers.heightSliderInput) {
      heightSlider.removeEventListener("input", boundHandlers.heightSliderInput);
    }
    boundHandlers.heightSliderInput = function () {
      const val = this.value;
      if (heightInput) heightInput.value = val;
      if (heightValue) heightValue.textContent = val;
      updateSizeResetButtonVisibility();
      renderModalPreview();
    };
    heightSlider.addEventListener("input", boundHandlers.heightSliderInput);
  }

  updateSizeResetButtonVisibility();
}

function getSliderValue(selector) {
  const slider = document.querySelector(selector);
  if (slider) {
    return parseInt(slider.value, 10);
  }
  // Fallback to hidden input
  const input = document.querySelector(selector.replace("-slider", ""));
  if (input) {
    return parseInt(input.value, 10);
  }
  return selector === WIDTH_SLIDER ? getDefaultWidth() : getDefaultHeight();
}

function updateSizeResetButtonVisibility() {
  const widthInput = document.querySelector(WIDTH_INPUT);
  const heightInput = document.querySelector(HEIGHT_INPUT);
  const sizeResetBtn = document.querySelector(SIZE_RESET);

  if (!sizeResetBtn) return;

  const currentWidth = parseInt(widthInput?.value, 10);
  const currentHeight = parseInt(heightInput?.value, 10);
  const defaultW = parseInt(sizeResetBtn.dataset.defaultWidth, 10) || getDefaultWidth();
  const defaultH = parseInt(sizeResetBtn.dataset.defaultHeight, 10) || getDefaultHeight();

  sizeResetBtn.classList.toggle(
    "is-invisible",
    currentWidth === defaultW && currentHeight === defaultH
  );
}

// =========================================================================
// Width: resizing barcode container width to label width; width validation
// =========================================================================

const getLabelWidth = () => {
  // Use the CSS variable if available (more reliable)
  // This is maintained by expose_width.js and updates automatically
  const root = document.documentElement;
  const cssWidth = getComputedStyle(root).getPropertyValue("--print-max-width");
  if (cssWidth) {
    const parsed = parseInt(cssWidth, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  // For supplement labels, use the label_width from the data object
  if (currentConfig === LABEL_CONFIGS.supplement) {
    const labelData = getLabelData();
    if (labelData?.label_width) {
      return labelData.label_width;
    }
  }

  // Fallback to direct measurement (for recipe labels)
  const labelDiv = document.querySelector(LABEL_DIV);
  if (!labelDiv) {
    // No warning needed - this is expected for supplement labels
    return DEFAULT_LABEL_WIDTH;
  }

  const rect = labelDiv.getBoundingClientRect();
  return Math.round(rect.width);
};

function updateMaxWidthIndicator() {
  const labelWidth = getLabelWidth();
  const indicator = document.querySelector(MAX_WIDTH_INDICATOR);

  if (!indicator || !labelWidth) return;

  indicator.classList.remove("is-hidden");
  indicator.style.width = `${labelWidth}px`;
}

function hideWidthWarningAndIndicators() {
  const warning = document.querySelector(WIDTH_WARNING);
  const indicator = document.querySelector(MAX_WIDTH_INDICATOR);
  const cautionIcon = document.querySelector(WIDTH_CAUTION_ICON);

  if (warning) warning.classList.add("is-hidden");
  if (indicator) indicator.classList.add("is-hidden");
  if (cautionIcon) cautionIcon.classList.add("is-invisible");
}

function updateLabelBarcodeContainerWidth() {
  const barcodeContainer = document.querySelector(BARCODE_CONTAINER);
  if (!barcodeContainer) return;

  // Use CSS to set the width based on the --print-max-width variable
  // This variable is maintained by expose_width.js via MutationObserver
  // and is more reliable than our own ResizeObserver approach
  barcodeContainer.style.width = "var(--print-max-width, 237px)";
}

// ==========================================
// Validation
// ==========================================

function validateBarcode() {
  const labelWidth = getLabelWidth();
  const barcodeSvg = document.querySelector(MODAL_SVG);
  const codeInput = document.querySelector(BARCODE_CODE);

  if (!barcodeSvg || !labelWidth || !codeInput) return false;

  const code = codeInput.value || "";
  const isLongerThanMinimum = code.trim().length >= MIN_BARCODE_CHARS;
  const barcodeWidth = barcodeSvg.getBBox().width;
  const isTooWide = !hasFormatError && barcodeWidth > labelWidth;

  const isValid = isLongerThanMinimum && !isTooWide && !hasFormatError;

  if (hasFormatError) {
    hideWidthWarningAndIndicators();
  } else {
    updateWidthIndicator({ isTooWide });
  }

  updateCodeErrorDisplay({
    isLongerThanMinimum,
    codeInput,
    code,
  });

  const applyButton = document.querySelector(APPLY_BUTTON);
  if (applyButton) applyButton.disabled = !isValid;

  return isValid;
}

function updateWidthIndicator({ isTooWide }) {
  if (hasFormatError) {
    hideWidthWarningAndIndicators();
    return;
  }

  const indicator = document.querySelector(MAX_WIDTH_INDICATOR);
  if (indicator) {
    indicator.classList.toggle("invalid", isTooWide);
    indicator.classList.remove("is-hidden");
  }

  const warning = document.querySelector(WIDTH_WARNING);
  const cautionIcon = document.querySelector(WIDTH_CAUTION_ICON);

  if (warning) warning.classList.toggle("is-hidden", !isTooWide);
  if (cautionIcon) cautionIcon.classList.toggle("is-invisible", !isTooWide);
}

function getReadableTypeName(type) {
  switch (type) {
    case CODE_128:
      return "Code 128";
    case UPC_A:
      return "UPC-A";
    case EAN_13:
      return "EAN-13";
    default:
      return type || DEFAULT_BARCODE_TYPE;
  }
}

function preValidateBarcodeFormat(code, type) {
  if (type !== UPC_A && type !== EAN_13) {
    return { isValid: true };
  }

  if (!/^\d+$/.test(code)) {
    return {
      isValid: false,
      errors: { nonNumeric: true, invalidLength: false, invalidCheckDigit: false },
      errorMessage: `${type === UPC_A ? "UPC-A" : "EAN-13"} must contain only digits`,
    };
  }

  const isValidLength =
    (type === UPC_A && (code.length === 11 || code.length === 12)) ||
    (type === EAN_13 && (code.length === 12 || code.length === 13));

  if (!isValidLength) {
    return {
      isValid: false,
      errors: { nonNumeric: false, invalidLength: true, invalidCheckDigit: false },
      errorMessage: `${
        type === UPC_A ? "UPC-A requires 11-12 digits" : "EAN-13 requires 12-13 digits"
      } (check digit optional)`,
    };
  }

  if ((type === UPC_A && code.length === 12) || (type === EAN_13 && code.length === 13)) {
    if (!validateCheckDigit(code, type)) {
      return {
        isValid: false,
        errors: { nonNumeric: false, invalidLength: false, invalidCheckDigit: true },
        errorMessage: `Invalid check digit. Note: you can use ${
          type === UPC_A ? "11" : "12"
        } digits to auto-calculate the check digit.`,
      };
    }
  }

  return {
    isValid: true,
    errors: { nonNumeric: false, invalidLength: false, invalidCheckDigit: false },
  };
}

function validateCheckDigit(code, type) {
  const sanitizedCode = removeSpacesAndDashes(code);

  // Return early if there is no 'check digit' to validate.
  if (
    (type === UPC_A && sanitizedCode.length !== 12) ||
    (type === EAN_13 && sanitizedCode.length !== 13)
  ) {
    return true;
  }

  const providedCheckDigit = parseInt(sanitizedCode.charAt(sanitizedCode.length - 1), 10);
  const numberWithoutCheckDigit = sanitizedCode.substring(0, sanitizedCode.length - 1);

  // Calculate the correct check digit using JsBarcode's function
  const calculatedCheckDigit = upcChecksum(numberWithoutCheckDigit);

  return providedCheckDigit === calculatedCheckDigit;
}

function updateCodeErrorDisplay({ isLongerThanMinimum, codeInput, code }) {
  const codeError = document.querySelector(BARCODE_CODE_ERROR);
  const codeCautionIcon = document.querySelector(CODE_CAUTION_ICON);
  const typeInput = document.querySelector(BARCODE_TYPE);
  const currentType = typeInput?.value || DEFAULT_BARCODE_TYPE;

  if (!codeError || !codeCautionIcon || !codeInput) return;

  let shouldShowError = false;
  let shouldShowRedOutline = false;

  // Don't show code length error if there's a format error
  if (hasFormatError) {
    codeError.classList.add("is-invisible");
    codeCautionIcon.classList.add("is-invisible");
    codeInput.classList.remove("has-error");
    return;
  }

  if (currentType === UPC_A || currentType === EAN_13) {
    const isFieldBlurred = document.activeElement !== codeInput;
    const sanitizedCode = removeSpacesAndDashes(code);

    if (code.trim().length > 0 && isFieldBlurred) {
      const validation = preValidateBarcodeFormat(sanitizedCode, currentType);

      if (!validation.isValid) {
        codeError.textContent = validation.errorMessage;
        shouldShowError = true;
      }
    }
  } else {
    if (code.trim().length > 0 && !isLongerThanMinimum) {
      codeError.textContent = `Must be at least ${MIN_BARCODE_CHARS} characters`;
      shouldShowError = true;
      shouldShowRedOutline = true;
    }
  }

  codeError.classList.toggle("is-invisible", !shouldShowError);
  codeCautionIcon.classList.toggle("is-invisible", !shouldShowError);

  codeInput.classList.toggle("has-error", shouldShowRedOutline);
}

/// Removes spaces and dashes for validation and rendering.
/// (We'll leave spaces or dashes in the persisted field so that the user sees
/// spaces and dashes if they entered them.)
function removeSpacesAndDashes(code) {
  return code.replace(/[\s-]+/g, "");
}

// ==========================================
// Initialization
// ==========================================

/// Main entry point for barcode section setup
export function renderLabelBarcodeSection() {
  const labelData = getLabelData();
  if (!labelData) return;

  const hideBarcode = labelData.hide_barcode;
  const checkbox = document.querySelector(SIDEBAR_BARCODE_TOGGLE);
  if (checkbox) checkbox.checked = hideBarcode;

  if (hideBarcode) {
    setBarcodeVisibility({ isVisible: false });
  } else {
    setBarcodeVisibility({ isVisible: true });
    updateLabelBarcodeContainerWidth();
  }
  renderLabelBarcode();
}

function fetchBarcodeCode(callback) {
  const labelData = getLabelData();
  if (!labelData) return;

  const labelId = labelData.id;

  fetch(getDefaultCodePath(labelId), {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
  })
    .then((response) => {
      if (!response.ok) throw response;
      return response.json();
    })
    .then((data) => {
      const barcode = data.barcode;
      if (!barcode || !barcode?.code) {
        console.error("Called /default_code endpoint but did not receive {barcode: { code: ...}}");
        return;
      }

      if (labelData.primary_barcode) {
        labelData.primary_barcode.code = barcode.code;
      }

      const codeInput = document.querySelector(BARCODE_CODE);
      if (codeInput) codeInput.value = barcode.code;

      renderModalPreview();
      callback && callback(barcode.code);
    })
    .catch(handleFetchError);
}

// ==========================================
// HTTP helpers
// ==========================================

function handleFetchError(error) {
  if (error instanceof Response) {
    error
      .json()
      .then((data) => {
        if (data.error) {
          Flash.error(data.error);
        } else {
          Flash.error("An error occurred. Please try again.");
        }
      })
      .catch(() => {
        Flash.error("An unexpected error occurred.");
      });
  } else {
    Flash.error("An unexpected error occurred.");
    console.error("Error details:", error);
  }

  // Request store reload for supplement labels after error
  if (currentConfig === LABEL_CONFIGS.supplement) {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("supplement:request-reload"));
    }, 500);
  }
}

function handleSuccess(data) {
  if (data.notice) {
    Flash.hideExisting();
    Flash.success(data.notice, { ignoreThrottle: true });
  }
}

// ==========================================
// Setup
// ==========================================

const setup = (labelType = "recipe") => {
  currentConfig = LABEL_CONFIGS[labelType] || LABEL_CONFIGS.recipe;

  renderLabelBarcodeSection();

  setupCheckbox();
  setupEditButton();
};

// Track partial:replace handler to prevent listener accumulation
let partialReplaceHandler = null;

export function initializeRecipeBarcode() {
  setup("recipe");

  // Remove previous listener if exists to prevent accumulation
  if (partialReplaceHandler) {
    document.removeEventListener("partial:replace", partialReplaceHandler);
  }
  partialReplaceHandler = () => setup("recipe");
  document.addEventListener("partial:replace", partialReplaceHandler);
}

// For supplement labels, Vue handles the sidebar but we need to initialize modal bindings
// when the modal is opened. This function sets the config and prepares the modal.
function initializeSupplementBarcodeModal() {
  currentConfig = LABEL_CONFIGS.supplement;

  // Ensure window.supplement_label exists
  const labelData = getLabelData();
  if (!labelData) {
    console.warn(
      "[barcode.js] Cannot initialize supplement modal: window.supplement_label not set"
    );
    return;
  }

  setupModalBindings();
  renderModalPreview();
  validateBarcode();
}

// Expose getBarcodeFormat globally for inline JS in ERB templates
window.getBarcodeFormat = getBarcodeFormat;

export { setup, initializeSupplementBarcodeModal };
